// ============================================================
// Chip presentation for {{...}} tokens on the canvas.
//
// STORED design content always holds raw token literals — the server,
// sanitizer, and renderer never see chips. These transforms run only at
// the canvas boundary: tokensToChips() when content enters a display div
// or the contenteditable overlay, chipsToTokens() when overlay HTML is
// committed back to component state. contenteditable="false" makes each
// chip atomic while editing (cursor hops over it, backspace removes the
// whole chip).
// ============================================================

import type { TokenResolutionData } from '@rh/shared/templates/design-tokens'
import { tokenFieldByToken } from './token-fields'

/** Mirrors TOKEN_PATTERN in design-tokens.ts; capture keeps delimiters. */
const TOKEN_SPLIT_RE = /(\{\{\s*[a-zA-Z]+\.[a-zA-Z]+\s*\}\})/g
/** Anchored, stateless twin for testing a single split part. */
const TOKEN_ONLY_RE = /^\{\{\s*([a-zA-Z]+)\.([a-zA-Z]+)\s*\}\}$/

/**
 * Chip markup for a known token; null for tokens we don't recognize
 * (those stay visible as literal text rather than an unlabeled chip).
 */
export function chipHtml(token: string): string | null {
  const field = tokenFieldByToken(token)
  if (!field) return null
  // The ▾ arrow is the click target for the fill-ins menu; clicking the
  // label body selects the chip for formatting instead (text-toolbar.ts).
  // Only the outer [data-token] element matters to chipsToTokens — the
  // inner structure is presentation.
  return (
    `<span class="rh-token-chip rh-chip-${field.scope}"` +
    ` contenteditable="false" data-token="${field.token}"` +
    ` title="${field.hint}">` +
    `<span class="rh-chip-label">${field.label}</span>` +
    '<span class="rh-chip-arrow" title="Change or remove this field">▾</span>' +
    '</span>'
  )
}

/**
 * Walks the content's text nodes and replaces each known-token literal
 * with whatever `renderToken` returns for it (null = leave as text).
 * Markup and attributes are never touched.
 */
function transformTokens(
  content: string,
  renderToken: (token: string) => Node | null
): string {
  if (!content.includes('{{')) return content
  const root = document.createElement('div')
  root.innerHTML = content
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  let node: Node | null
  while ((node = walker.nextNode())) textNodes.push(node as Text)

  for (const textNode of textNodes) {
    const value = textNode.nodeValue ?? ''
    if (!value.includes('{{')) continue
    // Defensive: never re-transform text already inside a chip.
    if (textNode.parentElement?.closest('[data-token]')) continue
    const parts = value.split(TOKEN_SPLIT_RE)
    if (parts.length === 1) continue
    const frag = document.createDocumentFragment()
    let replaced = false
    for (const part of parts) {
      if (!part) continue
      const rendered = TOKEN_ONLY_RE.test(part) ? renderToken(part) : null
      if (rendered) {
        frag.appendChild(rendered)
        replaced = true
      } else {
        frag.appendChild(document.createTextNode(part))
      }
    }
    if (replaced) textNode.replaceWith(frag)
  }
  return root.innerHTML
}

function chipElement(token: string): HTMLElement | null {
  const html = chipHtml(token)
  if (!html) return null
  const holder = document.createElement('div')
  holder.innerHTML = html
  return holder.firstElementChild as HTMLElement
}

/**
 * Replaces known token literals in stored content HTML with chip spans.
 */
export function tokensToChips(content: string): string {
  return transformTokens(content, chipElement)
}

/**
 * Display-mode rendering for an UNSELECTED text block: known tokens show
 * the value they resolve to for the current user — the canvas reads as a
 * real preview. Tokens with nothing saved render as a muted chip instead
 * so the line keeps its shape (and its meaning) while editing. Selecting
 * the block switches to full chips via tokensToChips.
 */
export function tokensToPreview(
  content: string,
  data: TokenResolutionData
): string {
  return transformTokens(content, token => {
    const m = token.match(TOKEN_ONLY_RE)
    const scope = m?.[1] as keyof TokenResolutionData | undefined
    const value =
      scope && m
        ? (
            data[scope] as Record<string, string | null | undefined> | undefined
          )?.[m[2]]?.trim()
        : undefined
    if (value) {
      // Marked as live (dotted underline + tooltip), not bare text: with
      // values resolved, nothing else would distinguish a dynamic field
      // from hardcoded text. Presentation-only — display divs are never
      // committed back to content.
      const field = tokenFieldByToken(token)
      const span = document.createElement('span')
      span.className = `rh-token-preview rh-preview-${field?.scope ?? 'agency'}`
      if (field) span.title = `${field.label} · ${field.hint}`
      span.textContent = value
      return span
    }
    const chip = chipElement(token)
    chip?.classList.add('rh-chip-empty')
    return chip
  })
}

/**
 * Re-points an existing chip element at a different token, updating the
 * attributes chipsToTokens and the styling rely on. No-op for unknown
 * tokens. Used by the chip click-menu ("change what this fills in with").
 */
export function swapChipToken(chip: HTMLElement, token: string): void {
  const field = tokenFieldByToken(token)
  if (!field) return
  chip.setAttribute('data-token', field.token)
  chip.className = `rh-token-chip rh-chip-${field.scope}`
  chip.title = field.hint
  const label = chip.querySelector('.rh-chip-label')
  if (label) label.textContent = field.label
  else chip.textContent = field.label
}

/**
 * Converts overlay HTML back to storable content: every chip becomes its
 * token literal (whatever its visible label ended up as — data-token is
 * authoritative), and the zero-width spaces used for cursor placement
 * after inserted chips are stripped.
 */
export function chipsToTokens(html: string): string {
  if (!html.includes('data-token') && !html.includes('\u200B')) return html
  const root = document.createElement('div')
  root.innerHTML = html
  for (const el of Array.from(root.querySelectorAll('[data-token]'))) {
    el.replaceWith(document.createTextNode(el.getAttribute('data-token') ?? ''))
  }
  // Merge the text nodes replaceWith left behind so the serialized HTML
  // is byte-stable across repeated round-trips.
  root.normalize()
  return root.innerHTML.replace(/\u200B/g, '')
}
